/**
 * projectService.ts
 * Supabase CRUD for the `projects` table.
 *
 * Table DDL (run once in Supabase SQL editor):
 *
 *   create table if not exists projects (
 *     id           text primary key,
 *     user_id      uuid not null references auth.users(id) on delete cascade,
 *     name         text not null,
 *     instructions text,
 *     connectors   text[] default '{}',
 *     created_at   timestamptz default now(),
 *     updated_at   timestamptz default now()
 *   );
 *   alter table projects enable row level security;
 *   create policy "Users can manage their own projects"
 *     on projects for all using (auth.uid() = user_id);
 *
 * conversations table needs a project_id column (nullable):
 *   alter table conversations add column if not exists project_id text references projects(id) on delete set null;
 */

import { getSupabase } from '../hooks/useAuth'

export interface Project {
  id: string
  name: string
  instructions?: string
  connectors?: string[]
  createdAt: Date
}

interface DbProject {
  id: string
  user_id: string
  name: string
  instructions: string | null
  connectors: string[] | null
  created_at: string
  updated_at: string
}

function dbToLocal(row: DbProject): Project {
  return {
    id: row.id,
    name: row.name,
    instructions: row.instructions ?? undefined,
    connectors: row.connectors ?? [],
    createdAt: new Date(row.created_at),
  }
}

// ── Fetch all projects for a user ─────────────────────────────
export async function fetchProjects(userId: string): Promise<Project[]> {
  const supabase = getSupabase()
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true })
  if (error || !data) return []
  return (data as DbProject[]).map(dbToLocal)
}

// ── Create a project ──────────────────────────────────────────
export async function createProjectInDb(
  userId: string,
  project: Project
): Promise<void> {
  const supabase = getSupabase()
  await supabase.from('projects').insert({
    id: project.id,
    user_id: userId,
    name: project.name,
    instructions: project.instructions ?? null,
    connectors: project.connectors ?? [],
  })
}

// ── Delete a project ──────────────────────────────────────────
export async function deleteProjectFromDb(projectId: string): Promise<void> {
  const supabase = getSupabase()
  await supabase.from('projects').delete().eq('id', projectId)
}

// ── Move a conversation to a project (or remove from project) ─
export async function moveConversationToProject(
  conversationId: string,
  projectId: string | null
): Promise<void> {
  const supabase = getSupabase()
  await supabase
    .from('conversations')
    .update({ project_id: projectId })
    .eq('id', conversationId)
}

// ── Fetch conversations for a specific project ────────────────
export async function fetchProjectConversations(projectId: string): Promise<string[]> {
  const supabase = getSupabase()
  const { data } = await supabase
    .from('conversations')
    .select('id')
    .eq('project_id', projectId)
  return (data ?? []).map((r: { id: string }) => r.id)
}
